import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Box, Typography } from '@mui/material';
import React, { useCallback, useState } from 'react';
import { StyledButton } from '../styles/StyledButtons';

interface VeraPromptContextGuideProps {
  markdown: string;
  title?: string;
}

export default function VeraPromptContextGuide({
  markdown,
  title = 'How this prompt is used — copy for AI Studio',
}: VeraPromptContextGuideProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard
      .writeText(markdown)
      .then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        /* clipboard may be unavailable */
      });
  }, [markdown]);

  if (!markdown.trim()) return null;

  return (
    <Box
      sx={{
        mb: 3,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
        bgcolor: 'grey.50',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          flexWrap: 'wrap',
          px: 1.5,
          py: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <StyledButton
          size="small"
          variant="outlined"
          startIcon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
          onClick={handleCopy}
        >
          {copied ? 'Copied' : 'Copy for AI Studio'}
        </StyledButton>
      </Box>
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 1.5,
          fontFamily: 'monospace',
          fontSize: '0.72rem',
          lineHeight: 1.55,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          maxHeight: 320,
          overflowY: 'auto',
          color: 'text.primary',
        }}
      >
        {markdown}
      </Box>
    </Box>
  );
}
